import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

import { AppSwitcherItem } from '@core/models/app-switcher.state';
import { isSafeRedirectUrl } from '@core/utils/url-validator';

import { IconComponent } from '../icon/icon.component';

@Component({
  selector: 'app-switcher',
  standalone: true,
  imports: [IconComponent, OverlayModule],
  templateUrl: './app-switcher.component.html',
  styleUrl: './app-switcher.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'close()'
  }
})
export class AppSwitcherComponent {
  readonly apps = input<AppSwitcherItem[]>([]);
  readonly hasApps = computed(() => this.apps().length > 0);
  protected readonly open = signal(false);

  // Rendered through the CDK overlay container so the panel escapes the toolbar's
  // stacking context and paints above sticky data-table headers (--z-sticky).
  protected readonly panelPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 }
  ];

  /** Right-click menu for a single tile — `origin` is the cursor point. */
  protected readonly contextMenu = signal<{ app: AppSwitcherItem; origin: { x: number; y: number } } | null>(null);

  protected readonly contextMenuPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' }
  ];

  toggle(): void {
    this.open.update(v => !v);
  }

  close(): void {
    this.closeContextMenu();
    this.open.set(false);
  }

  openContextMenu(app: AppSwitcherItem, event: MouseEvent): void {
    event.preventDefault();
    this.contextMenu.set({ app, origin: { x: event.clientX, y: event.clientY } });
  }

  closeContextMenu(): void {
    this.contextMenu.set(null);
  }

  openInNewTab(app: AppSwitcherItem): void {
    this.closeContextMenu();

    if (!isSafeRedirectUrl(app.url)) return;

    window.open(app.url, '_blank', 'noopener');
    this.close();
  }

  navigate(app: AppSwitcherItem, event: MouseEvent): void {
    event.preventDefault();

    if (!isSafeRedirectUrl(app.url)) return;

    if (event.ctrlKey || event.metaKey || event.button === 1) {
      window.open(app.url, '_blank', 'noopener');
    } else {
      window.location.href = app.url;
    }
    this.close();
  }
}
